/* ProjectsChoser.js */

import React, { useState, useEffect } from "react";
import { SimpleTreeView } from "@mui/x-tree-view/SimpleTreeView";
import { TreeItem } from "@mui/x-tree-view/TreeItem";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import ManageSearchOutlinedIcon     from "@mui/icons-material/ManageSearchOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import AutoStoriesOutlinedIcon         from "@mui/icons-material/AutoStoriesOutlined";
import AccountTreeOutlinedIcon          from "@mui/icons-material/AccountTreeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import HyperionTooltip from "../HyperionToolTip.js";
import "./ProjectsChoser.css";

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const hexToRgba = (hex, alpha) => {
  const a = clamp01(alpha);
  if (!hex || typeof hex !== "string") return `rgba(255,255,255,${a})`;

  const h = hex.replace("#", "").trim();
  if (h.length !== 6) return `rgba(255,255,255,${a})`;

  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);

  if ([r, g, b].some((v) => Number.isNaN(v))) return `rgba(255,255,255,${a})`;
  return `rgba(${r},${g},${b},${a})`;
};

const ProjectsChoser = ({
  selectedProjectToken,
  selectedAccountToken,
  treeData,
  selectedViewLabel,
  onAccountChange,
  onProjectChange,
  onViewChange,
  isProjectsOpen,
  customTheme,
  currentUserId,
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(isProjectsOpen);
  }, [isProjectsOpen]);

  // ── Colour mapping ────────────────────────────────────────────────
  const getViewTypeColor = (viewType) => {
    const submissionColour        = customTheme.submissionColour;
    const submissionsUnderReview  = customTheme.submissionsUnderReviewColour;
    const reviewColour            = customTheme.reviewColour;
    const mainColour              = customTheme.mainColour;
    const userColour              = customTheme.userColour;

    if (viewType === "submissions")              return submissionColour;
    if (viewType === "submissions_under_review") return submissionsUnderReview;
    if (viewType === "reviews")                  return reviewColour;
    if (viewType === "users")                    return userColour;
    return mainColour;
  };

  // ── Icon mapping ──────────────────────────────────────────────────
  const getViewTypeIcon = (viewType) => {
    if (viewType === "submissions")              return DescriptionOutlinedIcon;
    if (viewType === "submissions_under_review") return PlaylistAddCheckOutlinedIcon;
    if (viewType === "reviews")                  return ManageSearchOutlinedIcon;
    if (viewType === "users")                    return PeopleOutlinedIcon;
    return DashboardOutlinedIcon;
  };

  // ── Branch label (account / project rows — unchanged) ─────────────
  const BranchLabel = ({ text, Icon = AutoStoriesOutlinedIcon }) => (
    <span className="pcBranchRow">
      <Icon className="pcBranchIcon" />
      <span className="pcBranchText" title={text}>{text}</span>
    </span>
  );

  // ── View row ──────────────────────────────────────────────────────
  const ViewRow = ({ view }) => {
    const isSelected = view.label === selectedViewLabel;
    const typeColor  = getViewTypeColor(view.type);
    const TypeIcon   = getViewTypeIcon(view.type);

    const selectedTint = hexToRgba(typeColor, 0.22);
    const hoverTint    = hexToRgba(typeColor, 0.28);

    // All flags are pre-computed by buildTreeData — no permission logic here
    const isDefault      = !!view.defaultView;
    const isSystemView   = !!view.isSystemView;              // platform built-in
    const isLocked       = !!view.isLocked || isSystemView;  // system views are locked too
    const isMine         = !!view.isMine   && !isSystemView; // suppress if system (impossible)
    const isSharedWithMe = !!view.isSharedWithMe && !isMine; // suppress if I own it
    const canEdit        = !!view.canEdit;

    return (
      <span
        className={["pcViewRow", isSelected ? "isSelected" : ""].join(" ")}
        style={{
          "--typeColor":    typeColor,
          "--selectedTint": selectedTint,
          "--hoverTint":    hoverTint,
        }}
      >
        {/* Left colour rail */}
        <span className="pcViewRail" aria-hidden="true" />

        {/* Type icon */}
        <TypeIcon className="pcViewTypeIcon" aria-hidden="true" />

        {/* Label */}
        <span className="pcViewText" title={view.label}>{view.label}</span>

        {/* Right meta badges — all show independently, no priority exclusion */}
        <span className="pcViewMeta">
          {/* Default view marker — first so it's never missed */}
          {isDefault && (
            <HyperionTooltip title="Default view" variant="dark">
              <span className="pcMetaPill pcMetaPill--default">
                <StarOutlinedIcon className="pcMetaIcon pcMetaIcon--default" />
              </span>
            </HyperionTooltip>
          )}
          {/* Platform built-in — write=SYSTEM (shield, not lock — different semantic) */}
          {isSystemView && (
            <HyperionTooltip title="System view — platform managed, cannot be edited" variant="dark">
              <span className="pcMetaPill pcMetaPill--system">
                <ShieldOutlinedIcon className="pcMetaIcon pcMetaIcon--system" />
              </span>
            </HyperionTooltip>
          )}
          {/* Explicitly locked by owner — write=NONE */}
          {isLocked && (
            <HyperionTooltip title="Locked — editing has been disabled" variant="dark">
              <span className="pcMetaPill pcMetaPill--locked">
                <LockOutlinedIcon className="pcMetaIcon pcMetaIcon--locked" />
              </span>
            </HyperionTooltip>
          )}
          {/* Created by me */}
          {isMine && (
            <HyperionTooltip title="Created by you" variant="dark">
              <span className="pcMetaPill pcMetaPill--person">
                <PersonOutlineIcon className="pcMetaIcon pcMetaIcon--person" />
              </span>
            </HyperionTooltip>
          )}
          {/* Specifically shared with me */}
          {isSharedWithMe && (
            <HyperionTooltip title="Shared with you" variant="dark">
              <span className="pcMetaPill pcMetaPill--shared">
                <ShareOutlinedIcon className="pcMetaIcon pcMetaIcon--shared" />
              </span>
            </HyperionTooltip>
          )}
        </span>
      </span>
    );
  };

  // ── Flatten view sections without changing buildTreeData ──────────
  const getFlatViews = (project) => {
    if (Array.isArray(project?.viewSections)) {
      return project.viewSections.flatMap((section) =>
        Array.isArray(section?.views) ? section.views : []
      );
    }

    if (Array.isArray(project?.views)) {
      return [...project.views].sort((a, b) => {
        const TYPE_ORDER = [
          "submissions",
          "submissions_under_review",
          "reviews",
          "users",
        ];

        const getTypeOrder = (type) => {
          const index = TYPE_ORDER.indexOf(type);
          return index === -1 ? Number.MAX_SAFE_INTEGER : index;
        };

        const typeDiff = getTypeOrder(a.type) - getTypeOrder(b.type);
        if (typeDiff !== 0) return typeDiff;

        return String(a.label || "").localeCompare(String(b.label || ""));
      });
    }

    return [];
  };

  // ── Tree node builders (data logic unchanged) ──────────────────────
  const buildProjectNode = (project) => {
    const isSelectedProject = project.value === selectedProjectToken;
    const sortedViews = getFlatViews(project);

    return (
      <TreeItem
        key={project.value}
        itemId={project.value}
        label={<BranchLabel text={project.label} />}
        onClick={!isSelectedProject ? () => onProjectChange(project.value) : null}
        className="pcTreeItem pcTreeItem--project"
      >
        {isSelectedProject &&
          expanded &&
          sortedViews.map((view) => {
            const isSelectedView = view.label === selectedViewLabel;
            return (
              <TreeItem
                key={view.value}
                itemId={view.value}
                label={<ViewRow view={view} />}
                onClick={!isSelectedView ? () => onViewChange(view.label) : null}
                className="pcTreeItem pcTreeItem--view"
              />
            );
          })}
      </TreeItem>
    );
  };

  const buildTree = () => {
    if (!Array.isArray(treeData)) return null;

    // Multi-account mode
    if (treeData.length > 0 && treeData[0]?.projects !== undefined) {
      return treeData.map((account) => {
        const isSelectedAccount = account.value === selectedAccountToken;
        return (
          <TreeItem
            key={account.value}
            itemId={account.value}
            label={<BranchLabel text={account.label} Icon={AccountTreeOutlinedIcon} />}
            onClick={!isSelectedAccount ? () => onAccountChange(account.value) : null}
            className="pcTreeItem pcTreeItem--account"
          >
            {isSelectedAccount && (account.projects ?? []).map(buildProjectNode)}
          </TreeItem>
        );
      });
    }

    // Single-account mode
    return treeData.map(buildProjectNode);
  };

  const defaultExpandedItems = [];
  if (treeData?.[0]?.projects !== undefined) defaultExpandedItems.push(selectedAccountToken);
  defaultExpandedItems.push(selectedProjectToken);

  return (
    <div className={`project-choice-container ${expanded ? "project-choice-expanded" : ""}`}>
      <SimpleTreeView defaultExpandedItems={defaultExpandedItems}>{buildTree()}</SimpleTreeView>
    </div>
  );
};

export default ProjectsChoser;
